/**
 * "Explain this" for a downsizing plan, as numbered lines (downsizing rebuild, 21 Oct 2026; the agreed
 * mock-up's d-explain): each line says what they do, and what that does to the numbers, ending in what is
 * left to the family against staying put. The same reading for a tested plan and one built by hand (the
 * owner's Manual Control parity rule), from the plan's own inputs and figures. Presentation only.
 *
 * Told on aged care's ledger (owner, 22 Oct 2026: "all modules use consistent UI"): CauseEffectLedger's navy
 * numerals and hairlines, each line's lead in bold, the money story inside the "Put together" line, then the
 * "=" line against staying put. Its own small blue circles are gone.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { CauseEffectLedger, ChangeOrText, putTogetherLine, type LedgerChange } from '../../components/strategy/CauseEffectLedger.tsx';
import { dzMoveOfInputs, dzUsesOf, dzPlanWords } from './dzPlanWords.ts';

export interface DzExplainFigures { legacy: number | null; pension: number; cashflow: number }

interface DzExplainStepsProps {
    factFind: DraftFactFind;
    inputs: Record<string, unknown>;
    rentPerWeek?: number;
    plan: DzExplainFigures;
    baseline: DzExplainFigures | null;
    horizonYears: number;
}

/** One numbered line: the bold lead (what they do), then what it does to the numbers. */
export interface DzExplainLine { lead: string; body: string; /** The "from … to …" figure as data, for Explain this's colour. */ change?: LedgerChange }

const money = (n: number) => formatCurrency(Math.round(n));
const joined = (l: DzExplainLine) => (l.body ? `${l.lead} ${l.body}` : l.lead);

/**
 * The lines, as plain strings (the advice document and the tests read the same sentences), and as lead/body
 * pairs for the ledger. `steps` keeps the "Put together" sentence last for the strings' readers; the ledger
 * tells that line itself (putTogetherLine), with the money story inside it. `resultWhy` is the reason the
 * family is left less, said under the "=" line.
 */
export function dzExplainLines(p: Omit<DzExplainStepsProps, 'factFind'> & { factFind: DraftFactFind }): { steps: string[]; lines: DzExplainLine[]; result: string | null; resultWhy: string | null } {
    const v = householdVoice(p.factFind);
    const m = dzMoveOfInputs(p.inputs, { rentPerWeek: p.rentPerWeek });
    const u = dzUsesOf(p.inputs);
    // The household named once, at the start; after that, "they" and "their" (or "you" and "your").
    const They = v.They;
    const Names = v.you ? 'You' : v.Names;
    const sell = `${Names} sell${v.s}`;
    const lines: DzExplainLine[] = [];
    const push = (lead: string, body = '', change?: LedgerChange) => lines.push({ lead, body, ...(change ? { change } : {}) });
    const net = m.salePrice - m.sellingCosts;
    if (m.sellAndRent) {
        push(`${sell} for ${money(m.salePrice)} and pay ${money(m.sellingCosts)} in selling costs, leaving ${money(net)}.`, `Nothing is bought: ${v.they} rent${m.rentPerWeek > 0 ? ` at ${money(m.rentPerWeek)} a week, a cost that rises each year` : ', and no rent was entered, so none is charged'}.`);
    } else if (m.buyUpShortfall >= 1) {
        push(`${sell} for ${money(m.salePrice)} and pay ${money(m.sellingCosts)} in selling costs, leaving ${money(net)}`, '— all of it goes into the new home.');
        if (m.newHomePrice > net * 1.05) push(`The ${money(m.newHomePrice)} home costs more than that:`, `${money(m.buyUpShortfall)} of savings pay the rest${m.stampDuty > 0 ? `, with the ${money(m.stampDuty)} of stamp duty` : ''}. The duty is gone on the day; the rest is now in a home that isn't counted.`);
        else push(`${They} pay ${money(m.stampDuty)} of stamp duty from savings.`, 'It is gone on the day; it does not add to the home\'s value.');
    } else {
        push(`${sell} for ${money(m.salePrice)} and pay ${money(m.sellingCosts)} in selling costs, leaving ${money(net)};`, `${money(m.newHomePrice)} goes into the new home.`);
        if (m.stampDuty > 0) push(`${They} pay ${money(m.stampDuty)} of stamp duty from the sale.`, 'It is gone on the day.');
        else push('No stamp duty is in the figures:', 'no state was chosen.');
    }
    if (m.freedCapital >= 1) {
        const where = dzPlanWords({ inputs: p.inputs, rentPerWeek: p.rentPerWeek }, p.factFind).sub.replace(/^The sale (frees|nets)[^.]*\.\s*/, '').replace(/^No stamp duty[^.]*\.\s*/, '');
        push(`That frees ${money(m.freedCapital)},`, `which is counted for the Age Pension wherever it goes.${where ? ` ${where}` : ''}${u.bank >= 1 && !/stays in the bank/.test(where) ? ` ${money(u.bank)} stays in the bank.` : ''}`);
    }
    const b = p.baseline;
    if (b) {
        const d = p.plan.pension - b.pension;
        if (Math.abs(d) < 1) push(`${v.Their} Age Pension stays at ${money(p.plan.pension)} a year.`);
        else if (d > 0) push(`Less is counted, so ${v.their} Age Pension rises`, `from ${money(b.pension)} to ${money(p.plan.pension)} a year.`, { from: b.pension, to: p.plan.pension, unit: ' a year', better: 'up', after: '.' });
        else if (p.plan.pension < 1) push(`More is counted, so ${v.their} Age Pension of ${money(b.pension)} a year stops.`);
        else push(`More is counted, so ${v.their} Age Pension falls`, `from ${money(b.pension)} to ${money(p.plan.pension)} a year.`, { from: b.pension, to: p.plan.pension, unit: ' a year', better: 'up', after: '.' });
    }
    const steps = lines.map(joined);
    if (b) {
        steps.push(p.plan.cashflow >= 0
            ? `Put together, ${v.they} have ${money(p.plan.cashflow)} a year to spare in the first year (staying put, ${b.cashflow < 0 ? `short ${money(-b.cashflow)}` : money(b.cashflow)}).`
            : `Put together, ${v.they} are ${money(-p.plan.cashflow)} short in the first year, met from savings (staying put, ${b.cashflow < 0 ? `short ${money(-b.cashflow)}` : `${money(b.cashflow)} to spare`}).`);
    }
    let result: string | null = null;
    let resultWhy: string | null = null;
    if (p.plan.legacy != null) {
        const yrs = `${p.horizonYears} ${p.horizonYears === 1 ? 'year' : 'years'}`;
        const costs = m.sellingCosts + m.stampDuty;
        if (b?.legacy != null) {
            const delta = p.plan.legacy - b.legacy;
            const higher = p.plan.pension > b.pension + 1;
            if (delta < -1) {
                resultWhy = higher && costs > 0
                    ? `The higher pension doesn't make up for ${money(costs)} of duty and selling costs.`
                    : costs > 0 ? `The ${money(costs)} of duty and selling costs ${p.plan.pension < b.pension - 1 ? 'and the lower pension both come' : 'comes'} out of it.` : null;
                result = `After about ${yrs} the family is left ${money(p.plan.legacy)} — ${money(-delta)} less than staying put.${resultWhy ? ` ${resultWhy}` : ''}`;
            } else {
                result = `After about ${yrs} the family is left ${money(p.plan.legacy)} — ${money(delta)} more than staying put.`;
            }
        } else {
            result = `After about ${yrs} the family is left ${money(p.plan.legacy)}.`;
        }
    }
    return { steps, lines, result, resultWhy };
}

interface DzExplainProps extends DzExplainStepsProps {
    /** The money story (MoneyStoryBlock), told inside the "Put together" line; only when a year runs short. */
    moneyStory?: React.ReactNode;
    /** After the "=" line: the guaranteed income panel, the whys, the debts. */
    children?: React.ReactNode;
    /** The plan's pieces, explained above the lines (owner, 24 Oct 2026). */
    leverKinds?: ReadonlyArray<string>;
}

/** The intro under Explain this's title, in the household's voice (aged care's line, owner 22 Oct 2026). */
export function dzExplainIntro(factFind: DraftFactFind | null | undefined): string {
    return `Each line says what ${householdVoice(factFind).they} do, and what that does to the numbers. "Staying put" means keeping the home and changing nothing else.`;
}

const DzExplainSteps = ({ moneyStory = null, children, leverKinds = [], ...p }: DzExplainProps) => {
    const { lines, resultWhy } = dzExplainLines(p);
    const v = householdVoice(p.factFind);
    const nodes: React.ReactNode[] = lines.map((l) => <><strong className="text-slate-900">{l.lead}</strong><ChangeOrText change={l.change} text={l.body ? <> {l.body}</> : null} /></>);
    nodes.push(putTogetherLine({ cashflow: p.plan.cashflow, baseCashflow: p.baseline ? p.baseline.cashflow : null, they: v.they, baseWord: 'staying put', moneyStory }));
    return (
        <CauseEffectLedger
            testId="dz-explain-steps"
            lines={nodes}
            estate={p.plan.legacy ?? 0}
            baseEstate={p.baseline?.legacy ?? null}
            horizonYears={p.horizonYears}
            baseWord="staying put"
            leverKinds={leverKinds}
        >
            {resultWhy && <p className="mt-3 text-[14px] leading-relaxed text-slate-600" data-testid="dz-explain-result-why">{resultWhy}</p>}
            {children}
        </CauseEffectLedger>
    );
};

export default DzExplainSteps;
